import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Mail, RefreshCw, ChevronDown, ChevronRight, BellRing, AlertTriangle, Copy } from "lucide-react";
import { fmtDate } from "@/lib/format";

// Compact response roll-up shown at the top of the Auditions page. Groups
// every finalized cast offer by response state so staff see at a glance
// who still owes an answer and who has already declined.
const STATES = [
  { key: "awaiting", label: "Awaiting response", icon: Clock,       tint: "border-amber-500/30 text-amber-300 bg-amber-500/5"  },
  { key: "accepted", label: "Accepted",          icon: CheckCircle2, tint: "border-emerald-500/30 text-emerald-300 bg-emerald-500/5" },
  { key: "declined", label: "Declined",          icon: XCircle,      tint: "border-red-500/30 text-red-300 bg-red-500/5"    },
];

export default function AuditionResponsesPane({ productionId, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [nudging, setNudging] = useState(null);   // id currently being nudged

  const load = async () => {
    if (!productionId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/audition-responses");
      setData(data);
    } catch { /* staff can just live without this pane */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [productionId, refreshKey]);

  const nudge = async (row) => {
    setNudging(row.id);
    try {
      const { data } = await api.post(`/audition-responses/${row.id}/nudge`);
      if (data?.delivered === false) {
        // Show the provider's actual reason so the director can act. Rate
        // limits already auto-retry 3× server-side; if we still see one it
        // means the provider genuinely couldn't deliver — no soft-pedalling.
        toast.warning(`Send blocked: ${data.error || "provider refused"} — use Copy invite to send manually.`);
      } else {
        toast.success(`Reminder emailed to ${row.name}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send reminder");
    } finally { setNudging(null); }
  };

  // Bundle the offer as plaintext so the admin can paste it into their own
  // email client when Resend's deliverability protection has blocked us.
  const copyInvite = async (row) => {
    try {
      const { data } = await api.get(`/audition-responses/${row.id}/copy-invite`);
      const clip = `To: ${data.email}\nSubject: ${data.subject}\n\n${data.plaintext_body}`;
      try {
        await navigator.clipboard.writeText(clip);
        toast.success(`Invite for ${row.name} copied — paste into your email`);
      } catch {
        // Fallback: show the payload in a prompt so mobile Safari users can still grab it.
        window.prompt("Copy this invite:", clip);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't build invite");
    }
  };

  if (!data || data.total === 0) return null;

  return (
    <section
      data-testid="responses-pane"
      className="rounded-lg border border-white/10 bg-[#121212] p-4 md:p-5"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="responses-pane-toggle"
      >
        {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">Cast offer responses</span>
        <span className="text-[11px] text-zinc-500 font-mono">{data.total}</span>
        <div className="ml-auto flex items-center gap-1.5">
          {STATES.map((s) => {
            const n = (data[s.key] || []).length;
            return (
              <span
                key={s.key}
                data-testid={`responses-count-${s.key}`}
                className={`px-2 h-6 inline-flex items-center gap-1 rounded-full border text-[10px] uppercase tracking-widest ${s.tint}`}
              >
                <s.icon className="w-3 h-3" /> {s.label.split(" ")[0]} · {n}
              </span>
            );
          })}
          <button
            type="button"
            data-testid="responses-refresh"
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="w-6 h-6 rounded-md text-zinc-500 hover:text-white hover:bg-white/5 grid place-items-center"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </button>

      {open && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="responses-columns">
          {STATES.map((s) => {
            const items = data[s.key] || [];
            return (
              <div
                key={s.key}
                data-testid={`responses-column-${s.key}`}
                className={`rounded-md border p-3 ${s.tint}`}
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest mb-2">
                  <s.icon className="w-3 h-3" /> {s.label} · {items.length}
                </div>
                {items.length === 0 ? (
                  <div className="text-[11px] text-zinc-500 py-2">Nobody in this bucket yet.</div>
                ) : (
                  <ul className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                    {items.map((r) => (
                      <li key={r.id} data-testid={`responses-item-${r.id}`} className="text-xs text-zinc-200">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="truncate font-medium">
                            {r.name}
                            {r.notified_delivered === false && (
                              <span
                                data-testid={`responses-undelivered-${r.id}`}
                                title={r.notified_error || "Email provider refused delivery"}
                                className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest px-1 py-0.5 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300 align-middle"
                              >
                                <AlertTriangle className="w-2.5 h-2.5" /> not delivered
                              </span>
                            )}
                          </span>
                          <span className="text-[10px] text-zinc-500 shrink-0">
                            {r.responded_at ? fmtDate(r.responded_at.slice(0, 10)) : "—"}
                          </span>
                        </div>
                        <div className="text-[10px] text-zinc-500 truncate">
                          {r.role || "role TBD"}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <a
                            href={`mailto:${r.email}`}
                            className="text-[10px] text-zinc-500 hover:text-white inline-flex items-center gap-1"
                          >
                            <Mail className="w-2.5 h-2.5" /> {r.email}
                          </a>
                          <div className="ml-auto flex items-center gap-1">
                            {(s.key === "awaiting" || r.notified_delivered === false) && (
                              <button
                                type="button"
                                data-testid={`responses-copy-invite-${r.id}`}
                                onClick={() => copyInvite(r)}
                                className="text-[10px] px-2 h-6 rounded-full border border-white/15 text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1"
                                title="Copy invite text to send manually via your own email"
                              >
                                <Copy className="w-2.5 h-2.5" /> Copy invite
                              </button>
                            )}
                            {s.key === "awaiting" && (
                              <button
                                type="button"
                                data-testid={`responses-nudge-${r.id}`}
                                onClick={() => nudge(r)}
                                disabled={nudging === r.id}
                                className="text-[10px] px-2 h-6 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <BellRing className={`w-2.5 h-2.5 ${nudging === r.id ? "animate-pulse" : ""}`} />
                                {nudging === r.id ? "Sending…" : "Nudge"}
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
